import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import Kpi from "./Kpi";
import KpiDisclosure from "./KpiDisclosure";
import MethodologyNote from "./MethodologyNote";
import DeploymentScalePanel from "./DeploymentScalePanel";
import EmergingPatternPanel from "./EmergingPatternPanel";
import { SENTIMENT_COLORS, SENTIMENT_LABELS, SENTIMENT_ORDER } from "../lib/format";

// ---------------------------------------------------------------------------
// Evidence builders — map each KPI to (a) a one-sentence calc description
// and (b) the workshops + reasoning field that back it up.
// ---------------------------------------------------------------------------

function evidenceSentimentInterp(workshops, matchFn) {
  return workshops
    .filter(matchFn)
    .map((w) => ({ workshop: w, text: w.sentiment_reasoning || "—" }));
}

function evidenceSdnp(workshops) {
  return workshops
    .filter((w) => w.sdnp_or_planning_constraint_mentioned)
    .map((w) => ({
      workshop: w,
      text:
        w.wind_discussion_summary ||
        "Workshop explicitly cites SDNP or planning as a factor.",
    }));
}

function evidenceFewerLarger(workshops, implicitOnly = false) {
  return workshops
    .filter((w) =>
      implicitOnly
        ? w.prefers_fewer_larger_implicit && !w.prefers_fewer_larger_explicit
        : w.prefers_fewer_larger_explicit || w.prefers_fewer_larger_implicit
    )
    .map((w) => ({
      workshop: w,
      text:
        w.prefers_fewer_larger_reasoning ||
        (w.prefers_fewer_larger_explicit
          ? "Explicitly stated in the workshop report."
          : "Inferred from turbine configuration / scale selection."),
    }));
}

function evidenceClustering(workshops, implicitOnly = false) {
  return workshops
    .filter((w) =>
      implicitOnly
        ? w.prefers_clustering_implicit && !w.prefers_clustering_explicit
        : w.prefers_clustering_explicit || w.prefers_clustering_implicit
    )
    .map((w) => ({
      workshop: w,
      text:
        w.prefers_clustering_reasoning ||
        (w.prefers_clustering_explicit
          ? "Explicitly stated in the workshop report."
          : "Inferred from spatial grouping in the proposed configuration."),
    }));
}

// ---------------------------------------------------------------------------
// DualKpi — explicit + implicit KPI card with its own disclosure.
// ---------------------------------------------------------------------------

function DualKpi({
  label,
  explicit,
  withImplicit,
  sub,
  testid,
  disclosureCalc,
  disclosureEvidence,
  onSelect,
}) {
  return (
    <div
      className="bg-white border border-gray-200 p-5 flex flex-col gap-2"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          {label}
        </span>
        {disclosureCalc ? (
          <KpiDisclosure
            calc={disclosureCalc}
            evidence={disclosureEvidence || []}
            onSelect={onSelect}
            testid={`${testid}-disclosure`}
          />
        ) : null}
      </div>
      <div className="flex items-baseline gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400">
            Explicit
          </div>
          <div className="text-2xl font-bold font-mono text-gray-900">
            {explicit}%
          </div>
        </div>
        <div className="text-gray-300 font-mono text-lg">→</div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#2F4F4F]">
            Incl. implicit
          </div>
          <div className="text-2xl font-bold font-mono text-[#2F4F4F]">
            {withImplicit}%
          </div>
        </div>
      </div>
      <span className="text-[11px] text-gray-500 leading-snug">
        Implicit signal derived from turbine configurations, scale choices and
        siting behaviour.
      </span>
      {sub ? (
        <span className="text-xs text-gray-500 leading-snug">{sub}</span>
      ) : null}
    </div>
  );
}

function HeadlineFinding({ text, testid }) {
  return (
    <li
      className="border-l-2 border-[#2F4F4F] pl-3 py-1 text-sm text-gray-800"
      data-testid={testid}
    >
      {text}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OverviewTab({ stats, workshops, onSelect }) {
  if (!stats) return null;

  const sentimentData = SENTIMENT_ORDER.map((k) => ({
    key: k,
    name: SENTIMENT_LABELS[k] || k,
    count: stats.sentiment_counts?.[k] ?? 0,
    color: SENTIMENT_COLORS[k] || "#64748B",
  })).filter((d) => d.count > 0);

  const pct = stats.pct || {};
  const pctExp = stats.pct_explicit || {};
  const totals = stats.totals || {};

  // Disclosures
  const discStrongly = {
    calc: "Count of workshops whose interpretive sentiment classification is strongly_supportive, divided by total workshops loaded.",
    evidence: evidenceSentimentInterp(
      workshops,
      (w) => (w.sentiment_interpretive || w.sentiment) === "strongly_supportive"
    ),
  };
  const discSupportTotal = {
    calc: "Workshops engaging positively under the interpretive classification: strongly_supportive + supportive_conditional + supportive_but_constrained.",
    evidence: evidenceSentimentInterp(workshops, (w) => {
      const s = w.sentiment_interpretive || w.sentiment;
      return (
        s === "strongly_supportive" ||
        s === "supportive_conditional" ||
        s === "supportive_but_constrained"
      );
    }),
  };
  const discConstrainedContext = {
    calc: "Workshops where engagement is constructive yet delivery is limited by SDNP, landscape sensitivity or planning assumptions.",
    evidence: evidenceSentimentInterp(
      workshops,
      (w) =>
        (w.sentiment_interpretive || w.sentiment) === "supportive_but_constrained"
    ),
  };
  const discResistant = {
    calc: "Workshops classified as resistant under the interpretive read — i.e. actual rejection of onshore wind, not of a specific location.",
    evidence: evidenceSentimentInterp(
      workshops,
      (w) =>
        ["constrained", "resistant"].includes(
          w.sentiment_interpretive || w.sentiment
        )
    ),
  };
  const discSdnp = {
    calc: "Workshops that explicitly cite the South Downs National Park designation or another planning assumption as a factor shaping deployment.",
    evidence: evidenceSdnp(workshops),
  };
  const discConstructive = discConstrainedContext; // same set

  const discFewerLarger = {
    calc: "Prefer fewer, larger turbines — combining explicit statements with implicit signals (selected turbine size, minimised counts, efficiency-of-scale arguments).",
    evidence: evidenceFewerLarger(workshops),
  };
  const discClustering = {
    calc: "Prefer clustering — combining explicit statements with implicit signals (grouped deployments, infrastructure-aligned siting, avoidance of dispersal).",
    evidence: evidenceClustering(workshops),
  };

  const headlines = buildHeadlines(stats, workshops);

  return (
    <div className="space-y-10" data-testid="overview-tab">
      <MethodologyNote testid="methodology-note-overview" />

      <section>
        <SectionHeader
          title="Dataset at a glance"
          subtitle="Raw counts and proportions across all ingested workshops. Click 'Evidence' on any metric to see the workshops behind the number."
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Kpi
            label="Workshops loaded"
            value={`${stats.total_loaded}/${stats.total_target}`}
            sub="cumulative across all batches"
            testid="kpi-loaded"
          />
          <Kpi
            label="Reported attendance"
            value={totals.attendance_total || "—"}
            sub={`across ${totals.attendance_reported_count || 0} workshops reporting`}
            testid="kpi-attendance"
          />
          <Kpi
            label="% Strongly supportive"
            value={`${pct.strongly_supportive ?? 0}%`}
            sub={`Explicit only: ${pctExp.strongly_supportive ?? 0}%`}
            accent="text-emerald-700"
            testid="kpi-pct-support"
            disclosure={
              <KpiDisclosure
                calc={discStrongly.calc}
                evidence={discStrongly.evidence}
                onSelect={onSelect}
                testid="kpi-pct-support-disclosure"
              />
            }
          />
          <Kpi
            label="% Engaging positively (interpretive)"
            value={`${pct.supportive_total ?? 0}%`}
            sub={`Explicit only: ${pctExp.supportive_total ?? 0}% · behaviour-weighted read`}
            accent="text-emerald-700"
            testid="kpi-pct-supportive-total"
            disclosure={
              <KpiDisclosure
                calc={discSupportTotal.calc}
                evidence={discSupportTotal.evidence}
                onSelect={onSelect}
                testid="kpi-pct-supportive-total-disclosure"
              />
            }
          />
          <Kpi
            label="% Supportive, constrained by context"
            value={`${pct.supportive_but_constrained ?? 0}%`}
            sub="constructive engagement; SDNP/landscape context"
            accent="text-amber-700"
            testid="kpi-pct-constrained-context"
            disclosure={
              <KpiDisclosure
                calc={discConstrainedContext.calc}
                evidence={discConstrainedContext.evidence}
                onSelect={onSelect}
                testid="kpi-pct-constrained-context-disclosure"
              />
            }
          />
          <Kpi
            label="% Resistant (no constructive engagement)"
            value={`${pct.constrained_resistant ?? 0}%`}
            sub={`Explicit only: ${pctExp.constrained_resistant ?? 0}%`}
            accent="text-rose-700"
            testid="kpi-pct-constrained"
            disclosure={
              <KpiDisclosure
                calc={discResistant.calc}
                evidence={discResistant.evidence}
                onSelect={onSelect}
                testid="kpi-pct-constrained-disclosure"
              />
            }
          />
          <Kpi
            label="% Citing SDNP / planning"
            value={`${pct.sdnp_or_planning_mention ?? 0}%`}
            sub="a delivery constraint — not a proxy for rejection"
            testid="kpi-pct-sdnp"
            disclosure={
              <KpiDisclosure
                calc={discSdnp.calc}
                evidence={discSdnp.evidence}
                onSelect={onSelect}
                testid="kpi-pct-sdnp-disclosure"
              />
            }
          />
          <Kpi
            label="Constructive engagement despite constraint"
            value={`${pct.constrained_by_context_not_opposed ?? 0}%`}
            sub="workshops classified as supportive-but-constrained by context"
            testid="kpi-pct-constructive"
            disclosure={
              <KpiDisclosure
                calc={discConstructive.calc}
                evidence={discConstructive.evidence}
                onSelect={onSelect}
                testid="kpi-pct-constructive-disclosure"
              />
            }
          />
          <DualKpi
            label="Prefer fewer, larger turbines"
            explicit={pct.fewer_larger_explicit ?? 0}
            withImplicit={pct.fewer_larger_any ?? 0}
            testid="kpi-dual-fewer-larger"
            disclosureCalc={discFewerLarger.calc}
            disclosureEvidence={discFewerLarger.evidence}
            onSelect={onSelect}
          />
          <DualKpi
            label="Prefer clustering"
            explicit={pct.clustering_explicit ?? 0}
            withImplicit={pct.clustering_any ?? 0}
            testid="kpi-dual-clustering"
            disclosureCalc={discClustering.calc}
            disclosureEvidence={discClustering.evidence}
            onSelect={onSelect}
          />
        </div>
      </section>

      <DeploymentScalePanel />

      <section>
        <SectionHeader
          title="Sentiment distribution (interpretive)"
          subtitle="Behaviour-weighted classification. Conservative explicit-only figures are preserved in the KPI cards above."
        />
        <div
          className="bg-white border border-gray-200 p-5"
          data-testid="sentiment-chart"
        >
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sentimentData}
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E5E7EB"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#4B5563" }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={90}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: "#4B5563" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                  {sentimentData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <EmergingPatternPanel />

      <section data-testid="headlines-section">
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono tracking-[0.2em] text-[#2F4F4F]">
              FINDINGS
            </span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mt-2">
            Key headline findings
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Factual statements drawn from the extracted dataset. Each
            references both explicit and interpretive signals where
            appropriate.
          </p>
        </div>

        {headlines.length > 0 ? (
          <div className="space-y-4" data-testid="headline-findings">
            {/* Hero finding — the headline the report rests on */}
            <article
              className="bg-[#F8FAF9] border-l-4 border-[#2F4F4F] p-6 sm:p-8 relative"
              data-testid="headline-finding-0"
            >
              <span className="absolute top-4 right-5 text-[11px] font-mono tracking-wider text-gray-400">
                01 · HEADLINE
              </span>
              <p className="text-lg sm:text-xl text-gray-900 leading-relaxed font-medium max-w-4xl">
                {typeof headlines[0] === "string" ? headlines[0] : headlines[0].body}
              </p>
            </article>

            {/* Supporting findings */}
            {headlines.length > 1 ? (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {headlines.slice(1).map((h, i) => {
                  const n = i + 2;
                  const title = typeof h === "string" ? null : h.title;
                  const body = typeof h === "string" ? h : h.body;
                  const keyId = title || (typeof h === "string" ? h.slice(0, 40) : n);
                  return (
                    <li
                      key={keyId}
                      className="bg-white border border-gray-200 p-5 flex items-start gap-4"
                      data-testid={`headline-finding-${n - 1}`}
                    >
                      <span className="inline-flex h-7 w-7 items-center justify-center bg-[#2F4F4F] text-white font-mono text-[11px] flex-shrink-0">
                        {String(n).padStart(2, "0")}
                      </span>
                      <div>
                        {title ? (
                          <p className="text-sm font-semibold text-gray-900 leading-snug">
                            {title}
                          </p>
                        ) : null}
                        <p
                          className={
                            "text-sm text-gray-700 leading-relaxed " +
                            (title ? "mt-1" : "")
                          }
                        >
                          {body}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="text-xl font-semibold tracking-tight text-gray-900">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
      ) : null}
    </div>
  );
}

function buildHeadlines(stats, workshops) {
  const n = stats.total_loaded;
  if (!n) return [];
  const pct = stats.pct || {};
  const pctE = stats.pct_explicit || {};
  const list = [];

  // Hero: kept as a single long string (rendered as one paragraph).
  list.push(
    `Most workshops engage constructively with onshore wind when constraints are understood. ${pct.supportive_total ?? 0}% of workshops engage positively with onshore wind in the interpretive read, compared with ${pctE.supportive_total ?? 0}% on the conservative explicit read.`
  );

  if (pct.constrained_by_context_not_opposed !== undefined) {
    list.push({
      title: "Constructive engagement, not opposition",
      body: `${pct.constrained_by_context_not_opposed}% of workshops are classified as "supportive, constrained by context" — engagement is constructive; delivery is limited by SDNP, landscape sensitivity or planning assumptions, not by opposition to wind itself.`,
    });
  }

  if (pct.sdnp_or_planning_mention !== undefined) {
    list.push({
      title: "SDNP and planning are delivery constraints",
      body: `${pct.sdnp_or_planning_mention}% of workshops explicitly cite SDNP or planning. This is a delivery constraint, not a proxy for rejection.`,
    });
  }

  if (pct.fewer_larger_any !== undefined) {
    list.push({
      title: "Preference for fewer, larger turbines",
      body: `${pct.fewer_larger_explicit ?? 0}% explicit → ${pct.fewer_larger_any ?? 0}% including preferences inferred from design choices.`,
    });
  }
  if (pct.clustering_any !== undefined) {
    list.push({
      title: "Preference for clustering over dispersal",
      body: `${pct.clustering_explicit ?? 0}% explicit → ${pct.clustering_any ?? 0}% including grouping patterns implied in the configurations proposed.`,
    });
  }

  const topC = (stats.top_constraints || [])[0];
  if (topC) {
    const isVisual = topC.key === "visual_impact";
    list.push({
      title: isVisual
        ? "Visual impact is a concern"
        : `${topC.key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())} is a concern`,
      body: isVisual
        ? `Visual impact is the most frequently cited barrier (${topC.count} workshops). In a number of workshops, this is accompanied by a perception that development within the National Park or similar landscapes would be challenging or difficult to deliver, even if supported locally.`
        : `The most frequently cited barrier is "${topC.key.replace(/_/g, " ")}" (${topC.count} workshops).`,
    });
  }

  const topS = (stats.top_siting_logic || [])[0];
  if (topS) {
    list.push({
      title: "Most-common siting logic",
      body: `The most common siting logic is "${topS.key.replace(/_/g, " ")}" (${topS.count} workshops).`,
    });
  }

  return list;
}
